import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  hasPermission,
  hasRole,
  getNavigationForUser,
  canAccessRoute,
  canAccessModule,
  isReadOnly,
  isAdminLevel,
  ACCESS_DENIED_MESSAGES,
} from '../utils/permissions';

/**
 * Hook personnalisé pour gérer les permissions
 */
export function usePermissions() {
  const navigate = useNavigate();
  
  const user = useMemo(() => {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch {
      return null;
    }
  }, []);

  const checkPermission = (permission, redirectPath = '/dashboard', showToast = true) => {
    const allowed = hasPermission(user, permission);
    if (!allowed) {
      if (showToast) toast.error(ACCESS_DENIED_MESSAGES.default);
      if (redirectPath) navigate(redirectPath);
    }
    return allowed;
  };

  const checkRole = (allowedRoles, redirectPath = '/dashboard', showToast = true) => {
    const allowed = hasRole(user, allowedRoles);
    if (!allowed) {
      if (showToast) toast.error(ACCESS_DENIED_MESSAGES.view);
      if (redirectPath) navigate(redirectPath);
    }
    return allowed;
  };

  const checkRouteAccess = (path, redirectPath = '/dashboard') => {
    const allowed = canAccessRoute(user, path);
    if (!allowed) {
      const modules = user?.allowed_modules || [];
      if (modules.length === 0 && !isAdminLevel(user)) {
        toast.error(ACCESS_DENIED_MESSAGES.noModule);
      } else {
        toast.error(ACCESS_DENIED_MESSAGES.view);
      }
      navigate(redirectPath);
    }
    return allowed;
  };

  const checkModuleAccess = (requiredModules, redirectPath = '/dashboard') => {
    const allowed = canAccessModule(user, requiredModules);
    if (!allowed) {
      const modules = user?.allowed_modules || [];
      if (modules.length === 0 && !isAdminLevel(user)) {
        toast.error(ACCESS_DENIED_MESSAGES.noModule);
      } else {
        toast.error(ACCESS_DENIED_MESSAGES.view);
      }
      navigate(redirectPath);
    }
    return allowed;
  };

  return {
    user,
    hasPermission: (permission) => hasPermission(user, permission),
    hasRole: (allowedRoles) => hasRole(user, allowedRoles),
    hasModuleAccess: (moduleId) => canAccessModule(user, [moduleId]),
    checkPermission,
    checkRole,
    checkRouteAccess,
    checkModuleAccess,
    navigation: getNavigationForUser(user),
    isReadOnly: (context) => isReadOnly(user, context),
    canAccessRoute: (path) => canAccessRoute(user, path),
  };
}
